import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {startLoading, stopLoading} from "./loadingSlice";
import {GpsrService} from "../../api/generated/services/GpsrService";
import {GpsrCustomService} from "../../api/customServices/GpsrCustomService";
import type {GpsrProductListView} from "../../api/generated/models/GpsrProductListView";
import type {GpsrCreateProductRequest} from "../../api/generated/models/GpsrCreateProductRequest";
import {IQuestion} from "./gpsrQuestionsListSlice";
import {dateFormatted} from "../../helpers/dateFormatted";

interface ISetAddProduct {
    name: string;
    progress: number;
}

export interface ICreateProductResponse {
    status?: string;
    data?: {
        id?: string;
        name?: string;
        progress?: number;
        createdAt?: string;
    };
}

export interface IGpsrProduct {
    id: string;
    name: string;
    date: string;
    progress: number | undefined;
}

export interface IGpsrProductsSlice {
    products: IGpsrProduct[];
    isLoading: boolean;
    isError: boolean;
    error: string | null;
}

interface ICreateProductRequest {
    name: string;
    riskQuestions: IQuestion[];
}

interface IDeleteProductResponse {
    status?: string;
    message?: string;
}

const initialState: IGpsrProductsSlice = {
    products: [],
    isLoading: false,
    isError: false,
    error: null,
};

export const createProduct = createAsyncThunk(
    "gpsrProducts/createProduct",
    async (data: ICreateProductRequest, {dispatch}) => {
        dispatch(startLoading());

        try {
            const requestData: GpsrCreateProductRequest = {
                name: data.name,
                riskQuestions: data.riskQuestions.map(q => ({
                    topicId: q.topicId,
                    questionId: q.questionId,
                    pointId: q.pointId,
                    textarea: q.textarea,
                    files: q.files.map(f => ({
                        name: f.name,
                        originalName: f.originalName
                    }))
                }))
            };
            
            const response = await GpsrService.postAppGpsrProductCreate(requestData);
            dispatch(stopLoading());
            return { status: "success", data: { id: response.id, name: requestData.name } };
        } catch (err) {
            dispatch(stopLoading());
            throw err;
        } finally {
            dispatch(stopLoading());
        }
    }
);

export const deleteProduct = createAsyncThunk(
    "gpsrProducts/deleteProduct",
    async (productId: string, {dispatch}) => {
        dispatch(startLoading());

        try {
            const response = await GpsrService.deleteAppGpsrProductDelete(productId);
            return { status: "success", message: response.message };
        } catch (err) {
            dispatch(stopLoading());
            throw err;
        } finally {
            dispatch(stopLoading());
        }
    }
);

export const getAllProducts = createAsyncThunk(
    "gpsrProducts/getAllProducts",
    async (_, {dispatch, rejectWithValue}) => {
        dispatch(startLoading());

        try {
            const response = await GpsrService.getAppGpsrProductsList();
            dispatch(stopLoading());
            return response;
        } catch (e) {
            dispatch(stopLoading());
            return rejectWithValue(e instanceof Error ? e.message : "Unknown error");
        } finally {
            dispatch(stopLoading());
        }
    }
);

export const downloadProductDocumentZip = createAsyncThunk(
    "gpsrProducts/downloadProductDocumentZip",
    async (data: {productId: string, productName: string}, {rejectWithValue}) => {
        try {
            // Use custom service that properly handles Blob response
            await GpsrCustomService.gpsrProductDownloadDocumentation(data.productId, data.productName);
            return { success: true };
        } catch (error) {
            return rejectWithValue(
                error instanceof Error ? error.message : "Failed to download document"
            );
        }
    }
);

export const gpsrProductsSlice = createSlice({
    initialState,
    name: "gpsrProducts",
    reducers: {
        setAddProduct: (state, action: PayloadAction<ISetAddProduct>) => {
            const data = {
                id: (state.products.length + 1).toString(),
                name: action.payload.name,
                date: new Date().toLocaleString(),
                progress: action.payload.progress,
            };

            state.products.push(data);
        },

        setDeleteProduct: (state, action: PayloadAction<string | null>) => {
            if (action.payload !== null) {
                state.products = state.products.filter((product) => {
                    return product.id !== action.payload;
                });
            }
        },

        setIsError: (state, action: PayloadAction<boolean>) => {
            state.isError = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllProducts.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getAllProducts.fulfilled, (state, action) => {
                state.isLoading = false;
                state.products =
                    (action.payload as GpsrProductListView[])?.map((product) => ({
                        id: product.id || "",
                        name: product.name || "",
                        date: product.createdAt
                            ? dateFormatted(product.createdAt)
                            : new Date().toLocaleString(),
                        progress: product.progress,
                    })) ?? [];
            })
            .addCase(getAllProducts.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.error = action.payload as string;
            })
            .addCase(createProduct.rejected, (state, action) => {
                // Error handling
            })
            .addCase(downloadProductDocumentZip.rejected, (state, action) => {})
            .addCase(deleteProduct.rejected, (state, action) => {});
    },
});

export const {setAddProduct, setDeleteProduct, setIsError} =
    gpsrProductsSlice.actions;

export default gpsrProductsSlice.reducer;
